/* eslint-disable no-template-curly-in-string */
import React, { useCallback } from 'react';
import type { ReactNode } from 'react';
import { Box, Text as ChakraText, Flex, Link } from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router';
import { Link as ReactRouterLink } from 'react-router-dom';
import type { BoxProps } from '@chakra-ui/react';
import { useCustomerGet } from '../../bff-api/customers/useCustomerGet';
import { PERKS_MULTIPLIER } from '../../constants';
import { isRegistered } from '../../contexts/utils/customer-utils';
import { PageUrl } from '../../pages/utils/page-url';
import { Button, Header, Text } from '../../ui-components';
import { Drawer } from '../../ui-components/drawer';
import { useCustomPreferenceOrDefault, useUrl } from '../../utils/api';
import { CustomPreference } from '../../utils/api/CustomPreference';
import { LetterEmailIcon, PerksNotFilledIcon, SafoniaLogoIcon } from '../icons';
import { SoftLink } from '../soft-link/SoftLink';

interface SafoniaPerksBoxProps extends BoxProps {
    children: ReactNode;
    variant?: 'premium' | 'standard';
}

/**
 * Decorative wrapper that simulates a gradient (premium) or solid (standard) 1px border.
 * Chakra does not support gradient borders directly, so we render a padded container
 * with the gradient background and place the actual card inside.
 */
const SafoniaPerksBox = ({
    children,
    variant = 'standard',
    ...props
}: SafoniaPerksBoxProps): JSX.Element => (
    <Box
        p="1px"
        borderRadius="8px"
        {...(variant === 'premium'
            ? { bgGradient: 'linear(19deg, #8246E0 12.57%, #E44185 47.54%, #FF7806 100%)' }
            : { backgroundColor: 'slt-pepper.400' })}
        {...props}
    >
        {children}
    </Box>
);

type PerkConfig = {
    /** Base standard amount used for calculating premium value */
    amount: number;
    /** Threshold label used in the supporting line (e.g. $30, $40) */
    threshold: number;
};

type PerkTier = {
    /** i18n id for the spend header line */
    spendMessageId: string;
    /** Default for the spend header line */
    spendDefaultMessage: string;
    standard: PerkConfig;
    premium: PerkConfig;
};

const PERK_TIERS: PerkTier[] = [
    {
        spendMessageId: 'spend-up-to-50',
        spendDefaultMessage: 'Spend up to $50',
        standard: { amount: 5, threshold: 30 },
        premium: { amount: 5, threshold: 30 },
    },
    {
        spendMessageId: 'spend-up-to-100',
        spendDefaultMessage: 'Spend up to $100',
        standard: { amount: 10, threshold: 40 },
        premium: { amount: 10, threshold: 40 },
    },
    {
        spendMessageId: 'spend-up-to-150',
        spendDefaultMessage: 'Spend up to $150',
        standard: { amount: 15, threshold: 60 },
        premium: { amount: 15, threshold: 60 },
    },
    {
        spendMessageId: 'spend-up-to-250',
        spendDefaultMessage: 'Spend up to $250',
        standard: { amount: 20, threshold: 80 },
        premium: { amount: 20, threshold: 80 },
    },
    {
        spendMessageId: 'spend-250-more',
        spendDefaultMessage: 'Spend $250 or more',
        standard: { amount: 25, threshold: 100 },
        premium: { amount: 25, threshold: 100 },
    },
];

const StandardCard = ({
    id,
    amount,
    threshold,
    domId,
    formatComponent,
}: {
    id: string;
    amount: number;
    threshold: number;
    domId?: string;
    formatComponent: (chunks: ReactNode[]) => ReactNode;
}): JSX.Element => (
    <SafoniaPerksBox flex={1} variant="standard">
        <Flex
            direction="column"
            minW={0}
            gap={2}
            px={4}
            py={2}
            borderRadius={7}
            backgroundColor="white"
        >
            <ChakraText textStyle="all-caps-xs" fontWeight={500} color="slt-pepper.1000">
                <FormattedMessage id="perk-standard" defaultMessage="Standard" />
            </ChakraText>
            <ChakraText
                id={`${id}-${domId ?? `get-${amount}-off`}`}
                textStyle="body-base-pepper-1000"
            >
                <FormattedMessage
                    id="get-off-safonia"
                    defaultMessage="Get <component>${amount}</component> off"
                    values={{ component: formatComponent, amount }}
                />
            </ChakraText>
            <ChakraText
                textStyle="body-xs"
                id={`${id}-${domId ?? `get-${amount}-off`}-with-purchase`}
            >
                <FormattedMessage
                    id="get-off-safonia-with-purchase"
                    defaultMessage="with a ${threshold} purchase"
                    values={{ threshold }}
                />
            </ChakraText>
        </Flex>
    </SafoniaPerksBox>
);

const PremiumCard = ({
    id,
    baseAmount,
    threshold,
    domId,
    formatComponent,
}: {
    id: string;
    baseAmount: number;
    threshold: number;
    domId?: string;
    formatComponent: (chunks: ReactNode[]) => ReactNode;
}): JSX.Element => (
    <SafoniaPerksBox flex={1} variant="premium">
        <Flex
            direction="column"
            minW={0}
            gap={2}
            px={4}
            py={2}
            borderRadius={7}
            backgroundColor="#FFF8F3"
        >
            <Flex direction="row" gap={1}>
                <SafoniaLogoIcon width="11px" height="12px" />
                <ChakraText textStyle="all-caps-xs" fontWeight={500} color="slt-black.1000">
                    <FormattedMessage id="perk-safonia-premium" defaultMessage="Premium" />
                </ChakraText>
            </Flex>
            <ChakraText
                id={`${id}-${domId ?? `get-premium-${baseAmount}-off`}`}
                textStyle="body-base-pepper-1000"
            >
                <FormattedMessage
                    id="get-off-safonia-premium"
                    defaultMessage="Get <component>${premiumValue}</component> off"
                    values={{
                        component: formatComponent,
                        premiumValue: (baseAmount * PERKS_MULTIPLIER).toFixed(2),
                    }}
                />
            </ChakraText>
            <ChakraText
                textStyle="body-xs"
                id={`${id}-${domId ?? `get-premium-${baseAmount}-off`}-with-purchase`}
            >
                <FormattedMessage
                    id="get-off-safonia-with-purchase"
                    defaultMessage="with a ${threshold} purchase"
                    values={{ threshold }}
                />
            </ChakraText>
        </Flex>
    </SafoniaPerksBox>
);

interface Props {
    id: string;
    isOpen: boolean;
    onClose: () => void;
}

export const PerksSafoniaDrawer: React.FC<Props> = ({ id, isOpen, onClose }: Props) => {
    const intl = useIntl();
    const loginShowUrl = useUrl(PageUrl.LOGIN_SHOW);
    const { data: customer } = useCustomerGet();
    const location = useLocation();

    const component = useCallback(
        (chunks: ReactNode[]) => (
            <Text color="slt-primary-green.1000" style="bold">
                {chunks}
            </Text>
        ),
        [],
    );

    const isPwaAccountRedirectEnabled = useCustomPreferenceOrDefault(
        CustomPreference.PWA_ACCOUNT_REDIRECT,
        true,
    );

    if (!customer) {
        return null;
    }

    const redirectUrl = encodeURIComponent(`${location.pathname}${location.search}`);
    const loginUrl = `${
        isPwaAccountRedirectEnabled ? PageUrl.ACCOUNT_LOGIN : loginShowUrl
    }?redirect-url=${redirectUrl}`;
    const registerUrl = `${PageUrl.ACCOUNT_REGISTRATION}?redirect-url=${redirectUrl}`;

    const { firstName } = customer;
    const isReturningCustomer = isRegistered(customer);

    return (
        <Drawer
            id={id}
            title={intl.formatMessage({
                id: 'perks.drawer.title',
                defaultMessage: 'SLT Perks',
            })}
            isOpen={isOpen}
            onClose={onClose}
            isFullPage
        >
            <Flex direction="column" gap={8}>
                {isReturningCustomer ? (
                    <Flex
                        data-testid={`${id}-returning-customer`}
                        direction="column"
                        alignItems="center"
                        gap={3}
                    >
                        <Header as="h3" variant="header3" id={`${id}-first-name`}>
                            <FormattedMessage
                                id="returning-customer-firstName"
                                defaultMessage="Hi, {firstName}!"
                                values={{ firstName }}
                            />
                        </Header>

                        <Flex direction="column" alignItems="center">
                            <Text id={`${id}-earn-perks`}>
                                <FormattedMessage
                                    id="returning-customer-earn-perks"
                                    defaultMessage="Earn Perks with your next purchase."
                                />
                            </Text>

                            <Text id={`${id}-redeem-perks`} align="center">
                                <FormattedMessage
                                    id="returning-customer-redeem-perks"
                                    defaultMessage="Redeem your earned Perks on any product online and/or in-store."
                                />
                            </Text>
                        </Flex>
                    </Flex>
                ) : (
                    <Flex
                        data-testid={`${id}-guest-customer`}
                        direction="column"
                        alignItems="center"
                        gap={3}
                    >
                        <Header as="h3" variant="header3" id={`${id}-ways-to-redeem`}>
                            <FormattedMessage
                                id="guest-customer-ways-to-redeem"
                                defaultMessage="Ways To Redeem"
                            />
                        </Header>

                        <Flex direction="column" alignItems="center" gap={6}>
                            <Text id={`${id}-redeem-your-earned-perks`} align="center">
                                <FormattedMessage
                                    id="guest-customer-redeem-your-earned-perks"
                                    defaultMessage="Redeem your earned Perks on any product Online and/or In-Store"
                                />
                            </Text>

                            <SoftLink data-testid={`${id}-sign-up-link`} to={registerUrl}>
                                <Button id={`${id}-sign-up`} variant="primary-black">
                                    <FormattedMessage
                                        id="guest-customer-sign-up"
                                        defaultMessage="Sign Up"
                                    />
                                </Button>
                            </SoftLink>
                        </Flex>

                        <Flex alignItems="center" gap={1}>
                            <Text id={`${id}-already-a-member`}>
                                <FormattedMessage
                                    id="guest-customer-already-a-member"
                                    defaultMessage="Already a member?"
                                />
                            </Text>

                            <Link
                                data-testid={`${id}-log-in-here-link`}
                                /** When isPwaAccountRedirectEnabled is cleaned, this Link should be a SoftLink (for consistency) */
                                {...(isPwaAccountRedirectEnabled
                                    ? { as: ReactRouterLink, to: loginUrl }
                                    : { href: loginUrl })}
                                _hover={{ textDecoration: 'none' }}
                            >
                                <Text id={`${id}-log-in-here`} type="link" style="bold">
                                    <FormattedMessage
                                        id="guest-customer-log-in-here"
                                        defaultMessage="Log in here"
                                    />
                                </Text>
                            </Link>
                        </Flex>
                    </Flex>
                )}
                <Box borderBottomWidth={1} borderColor="slt-pepper.300" />
                <Flex direction="column" gap={4}>
                    <Text id={`${id}-how-it-works`} textTransform="uppercase" style="bold">
                        <FormattedMessage id="how-it-works" defaultMessage="How it works" />
                    </Text>

                    <Flex direction="column" gap={6}>
                        <Flex alignItems="center" gap={4}>
                            <Flex
                                backgroundColor="slt-beige.400"
                                borderRadius={100}
                                width={20}
                                height={20}
                                flexShrink={0}
                                alignItems="center"
                                justifyContent="center"
                            >
                                <PerksNotFilledIcon data-testid={`${id}-perks-not-filled-icon`} />
                            </Flex>

                            <Flex direction="column" gap={1}>
                                <Text id={`${id}-earn-perks-every-3-months`} style="bold">
                                    <FormattedMessage
                                        id="earn-perks-every-3-months"
                                        defaultMessage="Earn Perks Every 3 Months:"
                                    />
                                </Text>

                                <Text id={`${id}-your-perks-3-month-period`}>
                                    <FormattedMessage
                                        id="your-perks-calculated-3-month-period"
                                        defaultMessage="Your Perks are calculated over a 3-month period."
                                    />
                                </Text>
                            </Flex>
                        </Flex>

                        <Flex alignItems="center" gap={4}>
                            <Flex
                                backgroundColor="slt-beige.400"
                                borderRadius={100}
                                width={20}
                                height={20}
                                flexShrink={0}
                                alignItems="center"
                                justifyContent="center"
                            >
                                <LetterEmailIcon data-testid={`${id}-letter-email-icon`} />
                            </Flex>

                            <Flex direction="column" gap={1}>
                                <Text id={`${id}-receive-promo-codes`} style="bold">
                                    <FormattedMessage
                                        id="receive-promo-codes-via-email"
                                        defaultMessage="Receive Promo Codes via Email:"
                                    />
                                </Text>

                                <Text id={`${id}-send-your-perks`}>
                                    <FormattedMessage
                                        id="send-your-perks-your-inbox"
                                        defaultMessage="We'll send your Perks directly to your inbox, so be sure to use the same email at checkout!"
                                    />
                                </Text>
                            </Flex>
                        </Flex>
                    </Flex>
                </Flex>
                <Flex
                    direction="column"
                    backgroundColor="slt-beige.400"
                    borderRadius={16}
                    padding={4}
                >
                    <Text id={`${id}-reward-levels`} textTransform="uppercase" style="bold">
                        <FormattedMessage id="reward-levels" defaultMessage="Reward levels" />
                    </Text>

                    <Flex direction="row" alignItems="center" gap={2} my={4}>
                        <SafoniaLogoIcon width="30px" height="32px" />
                        <Flex direction="column" justifyContent="center">
                            <ChakraText textStyle="protection-coverage-subtitle">
                                Premium with Safonia Protection
                            </ChakraText>
                            <ChakraText
                                textStyle="protection-coverage-text"
                                id={`${id}-safonia-perks-description`}
                            >
                                <FormattedMessage
                                    id="safonia-perks-description"
                                    defaultMessage="Get 25% more rewards on every purchase!"
                                />
                            </ChakraText>
                        </Flex>
                    </Flex>

                    <Flex direction="column" gap={6}>
                        <Flex direction="column" gap={2}>
                            <Text id={`${id}-spend-up-to-50`} style="bold">
                                <FormattedMessage
                                    id="spend-up-to-50"
                                    defaultMessage="Spend up to $50"
                                />
                            </Text>
                            <Flex direction="row" gap={2} w="100%">
                                <StandardCard
                                    id={id}
                                    amount={PERK_TIERS[0].standard.amount}
                                    threshold={PERK_TIERS[0].standard.threshold}
                                    domId="get-5-off"
                                    formatComponent={component}
                                />
                                <PremiumCard
                                    id={id}
                                    baseAmount={PERK_TIERS[0].premium.amount}
                                    threshold={PERK_TIERS[0].premium.threshold}
                                    domId="get-5-off"
                                    formatComponent={component}
                                />
                            </Flex>
                        </Flex>

                        <Flex direction="column" gap={2}>
                            <Text id={`${id}-spend-up-to-100`} style="bold">
                                <FormattedMessage
                                    id="spend-up-to-100"
                                    defaultMessage="Spend up to $100"
                                />
                            </Text>
                            <Flex direction="row" gap={2} w="100%">
                                <StandardCard
                                    id={id}
                                    amount={PERK_TIERS[1].standard.amount}
                                    threshold={PERK_TIERS[1].standard.threshold}
                                    domId="get-10-off"
                                    formatComponent={component}
                                />
                                <PremiumCard
                                    id={id}
                                    baseAmount={PERK_TIERS[1].premium.amount}
                                    threshold={PERK_TIERS[1].premium.threshold}
                                    domId="get-12-off"
                                    formatComponent={component}
                                />
                            </Flex>
                        </Flex>

                        <Flex direction="column" gap={2}>
                            <Text id={`${id}-spend-up-to-150`} style="bold">
                                <FormattedMessage
                                    id="spend-up-to-150"
                                    defaultMessage="Spend up to $150"
                                />
                            </Text>
                            <Flex direction="row" gap={2} w="100%">
                                <StandardCard
                                    id={id}
                                    amount={PERK_TIERS[2].standard.amount}
                                    threshold={PERK_TIERS[2].standard.threshold}
                                    domId="get-15-off"
                                    formatComponent={component}
                                />
                                <PremiumCard
                                    id={id}
                                    baseAmount={PERK_TIERS[2].premium.amount}
                                    threshold={PERK_TIERS[2].premium.threshold}
                                    domId="get-19-off"
                                    formatComponent={component}
                                />
                            </Flex>
                        </Flex>

                        <Flex direction="column" gap={2}>
                            <Text id={`${id}-spend-up-to-250`} style="bold">
                                <FormattedMessage
                                    id="spend-up-to-250"
                                    defaultMessage="Spend up to $250"
                                />
                            </Text>
                            <Flex direction="row" gap={2} w="100%">
                                <StandardCard
                                    id={id}
                                    amount={PERK_TIERS[3].standard.amount}
                                    threshold={PERK_TIERS[3].standard.threshold}
                                    domId="get-20-off"
                                    formatComponent={component}
                                />
                                <PremiumCard
                                    id={id}
                                    baseAmount={PERK_TIERS[3].premium.amount}
                                    threshold={PERK_TIERS[3].premium.threshold}
                                    domId="get-25-off"
                                    formatComponent={component}
                                />
                            </Flex>
                        </Flex>

                        <Flex direction="column" gap={2}>
                            <Text id={`${id}-spend-250-more`} style="bold">
                                <FormattedMessage
                                    id="spend-250-more"
                                    defaultMessage="Spend $250 or more"
                                />
                            </Text>
                            <Flex direction="row" gap={2} w="100%">
                                <StandardCard
                                    id={id}
                                    amount={PERK_TIERS[4].standard.amount}
                                    threshold={PERK_TIERS[4].standard.threshold}
                                    domId="get-25-off-standard"
                                    formatComponent={component}
                                />
                                <PremiumCard
                                    id={id}
                                    baseAmount={PERK_TIERS[4].premium.amount}
                                    threshold={PERK_TIERS[4].premium.threshold}
                                    domId="get-31-off"
                                    formatComponent={component}
                                />
                            </Flex>
                        </Flex>
                    </Flex>
                </Flex>
                <SoftLink data-testid={`${id}-learn-more-link`} to={PageUrl.PERKS}>
                    <Text id={`${id}-learn-more-message`} type="link" style="bold">
                        <FormattedMessage
                            id="learn-more-about-slt-perks"
                            defaultMessage="Learn more about SLT Perks"
                        />
                    </Text>
                </SoftLink>
            </Flex>
        </Drawer>
    );
};
